/**
 * src/components/student-dashboard-components/theme/responsive.js
 *
 * React Component Module.
 *
 * Purpose: React UI module containing one or more components and local UI helpers.
 *
 * Data flow (high level):
 * - UI/Caller -> this module -> downstream helpers/services -> state/response.
 */
import { themeConfig } from './themeConfig';

/**
 * Use Responsive.
 *
 * React component or UI helper used to render and manage local UI interactions.
 */


export const useResponsive = () => {
  const { breakpoints } = themeConfig;
  
  const queries = {
    xs: `(max-width: ${parseInt(breakpoints.sm) - 1}px)`,
    sm: `(min-width: ${breakpoints.sm}) and (max-width: ${parseInt(breakpoints.md) - 1}px)`,
    md: `(min-width: ${breakpoints.md}) and (max-width: ${parseInt(breakpoints.lg) - 1}px)`,
    lg: `(min-width: ${breakpoints.lg}) and (max-width: ${parseInt(breakpoints.xl) - 1}px)`,
    xl: `(min-width: ${breakpoints.xl})`,
    mobile: `(max-width: ${parseInt(breakpoints.md) - 1}px)`,
    tablet: `(min-width: ${breakpoints.md}) and (max-width: ${parseInt(breakpoints.lg) - 1}px)`,
    desktop: `(min-width: ${breakpoints.lg})`
  };
  
  return queries;
};

/**
 * Create Responsive Styles.
 *
 * React component or UI helper used to render and manage local UI interactions.
 */


export const createResponsiveStyles = (styles) => {
  const { breakpoints } = themeConfig;
  
  const queries = {
    xs: `(max-width: ${parseInt(breakpoints.sm) - 1}px)`,
    sm: `(min-width: ${breakpoints.sm}) and (max-width: ${parseInt(breakpoints.md) - 1}px)`,
    md: `(min-width: ${breakpoints.md}) and (max-width: ${parseInt(breakpoints.lg) - 1}px)`,
    lg: `(min-width: ${breakpoints.lg}) and (max-width: ${parseInt(breakpoints.xl) - 1}px)`,
    xl: `(min-width: ${breakpoints.xl})`,
    mobile: `(max-width: ${parseInt(breakpoints.md) - 1}px)`,
    tablet: `(min-width: ${breakpoints.md}) and (max-width: ${parseInt(breakpoints.lg) - 1}px)`,
    desktop: `(min-width: ${breakpoints.lg})`
  };
  
  const result = {};
  
  Object.keys(styles).forEach(breakpoint => {
    if (queries[breakpoint]) {
      result[`@media ${queries[breakpoint]}`] = styles[breakpoint];
    } else {
      result[breakpoint] = styles[breakpoint];
    }
  });
  
  return result;
};

/**
 * Responsive Value.
 *
 * React component or UI helper used to render and manage local UI interactions.
 */


export const responsiveValue = (values) => {
  const { xs, sm, md, lg, xl, mobile, tablet, desktop } = values;
  
  return {
    ...(xs && { [`@media (max-width: ${parseInt(themeConfig.breakpoints.sm) - 1}px)`]: xs }),
    ...(sm && { [`@media (min-width: ${themeConfig.breakpoints.sm}) and (max-width: ${parseInt(themeConfig.breakpoints.md) - 1}px)`]: sm }),
    ...(md && { [`@media (min-width: ${themeConfig.breakpoints.md}) and (max-width: ${parseInt(themeConfig.breakpoints.lg) - 1}px)`]: md }),
    ...(lg && { [`@media (min-width: ${themeConfig.breakpoints.lg}) and (max-width: ${parseInt(themeConfig.breakpoints.xl) - 1}px)`]: lg }),
    ...(xl && { [`@media (min-width: ${themeConfig.breakpoints.xl})`]: xl }),
    ...(mobile && { [`@media (max-width: ${parseInt(themeConfig.breakpoints.md) - 1}px)`]: mobile }),
    ...(tablet && { [`@media (min-width: ${themeConfig.breakpoints.md}) and (max-width: ${parseInt(themeConfig.breakpoints.lg) - 1}px)`]: tablet }),
    ...(desktop && { [`@media (min-width: ${themeConfig.breakpoints.lg})`]: desktop })
  };
};

/**
 * Mobile First.
 *
 * React component or UI helper used to render and manage local UI interactions.
 */


export const mobileFirst = (styles) => {
  const breakpointOrder = ['xs', 'sm', 'md', 'lg', 'xl'];
  const result = {};
  
  breakpointOrder.forEach(bp => {
    if (styles[bp]) {
      if (bp === 'xs') {
        Object.assign(result, styles[bp]);
      } else {
        result[`@media (min-width: ${themeConfig.breakpoints[bp]})`] = styles[bp];
      }
    }
  });
  
  return result;
};

/**
 * Desktop First.
 *
 * React component or UI helper used to render and manage local UI interactions.
 */


export const desktopFirst = (styles) => {
  const breakpointOrder = ['xl', 'lg', 'md', 'sm', 'xs'];
  const result = {};
  
  breakpointOrder.forEach(bp => {
    if (styles[bp]) {
      if (bp === 'xl') {
        Object.assign(result, styles[bp]);
      } else {
        const maxWidth = bp === 'xs' ? themeConfig.breakpoints.sm : 
                       bp === 'sm' ? themeConfig.breakpoints.md :
                       bp === 'md' ? themeConfig.breakpoints.lg :
                       themeConfig.breakpoints.xl;
        result[`@media (max-width: ${parseInt(maxWidth) - 1}px)`] = styles[bp];
      }
    }
  });
  
  return result;
};

export const containerStyles = {
  maxWidth: '100%',
  marginLeft: 'auto',
  marginRight: 'auto',
  paddingLeft: themeConfig.spacing.md,
  paddingRight: themeConfig.spacing.md,
  
  [`@media (min-width: ${themeConfig.breakpoints.sm})`]: {
    maxWidth: '540px'
  },
  [`@media (min-width: ${themeConfig.breakpoints.md})`]: {
    maxWidth: '720px'
  },
  [`@media (min-width: ${themeConfig.breakpoints.lg})`]: {
    maxWidth: '960px'
  },
  [`@media (min-width: ${themeConfig.breakpoints.xl})`]: {
    maxWidth: '1140px'
  },
  [`@media (min-width: ${themeConfig.breakpoints['2xl']})`]: {
    maxWidth: '1320px'
  }
};

/**
 * Grid Styles.
 *
 * React component or UI helper used to render and manage local UI interactions.
 */


export const gridStyles = (cols = {}) => {
  const { xs = 1, sm, md, lg, xl } = cols;
  
  return {
    display: 'grid',
    gridTemplateColumns: `repeat(${xs}, 1fr)`,
    gap: themeConfig.spacing.md,
    
    ...(sm && {
      [`@media (min-width: ${themeConfig.breakpoints.sm})`]: {
        gridTemplateColumns: `repeat(${sm}, 1fr)`
      }
    }),
    ...(md && {
      [`@media (min-width: ${themeConfig.breakpoints.md})`]: {
        gridTemplateColumns: `repeat(${md}, 1fr)`
      }
    }),
    ...(lg && {
      [`@media (min-width: ${themeConfig.breakpoints.lg})`]: {
        gridTemplateColumns: `repeat(${lg}, 1fr)`
      }
    }),
    ...(xl && {
      [`@media (min-width: ${themeConfig.breakpoints.xl})`]: {
        gridTemplateColumns: `repeat(${xl}, 1fr)`
      }
    })
  };
};